// STEP UX-2 (2026-09-10 §7·§8·§9) — LIVE-only view.
// Round 5 (2026-09-10 §6-§11) — 검색 + 6-category chips 추가 (client filter).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Search } from 'lucide-react';
import api from '@/lib/api';
import HomeLiveCard from '@/components/HomeLiveCard';
import { LIVEMOA_CATEGORY_OPTIONS, LIVEMOA_CATEGORY_LABEL, normalizeLivemoaCategory } from '@/lib/livemoaCategory';

const PAGE_LIMIT = 20;

const LiveOnly = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');

  const fetchPage = useCallback(async (append) => {
    setLoading(true);
    try {
      const offset = append ? items.length : 0;
      const r = await api.get('/live/live-now', { params: { limit: PAGE_LIMIT, offset } });
      const next = r.data?.items || [];
      setItems(append ? [...items, ...next] : next);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
    } catch (e) { console.error('live-only fetch failed:', e); }
    finally { setLoading(false); }
  }, [items]);

  useEffect(() => { fetchPage(false); /* eslint-disable-next-line */ }, []);

  // Round 5 §8·§9·§10 · client-side filter (검색어 + 6-category)
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== 'all' && normalizeLivemoaCategory(it) !== cat) return false;
      if (!needle) return true;
      const hay = ((it.title || '') + ' ' + (it.seller_name || '') + ' ' + (it.product_name || '') + ' ' + (it.category || '')).toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q, cat]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-20" data-testid="live-only-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white mb-3" data-testid="live-only-back">
          <ChevronLeft size={14} /> 홈으로
        </Link>
        <div className="flex items-baseline gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="live-only-title">🔴 지금 LIVE</h1>
          <span className="text-xs sm:text-sm text-gray-500" data-testid="live-only-count">{total}개 방송중</span>
        </div>

        {/* §9 · Search input · Mobile-first */}
        <div className="relative mb-3" data-testid="live-only-search-wrap">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="방송, 상품, 판매자 검색"
            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-sm focus:outline-none focus:border-[#FF3B30]"
            data-testid="live-only-search"
          />
        </div>

        {/* §7 · Category chips · horizontal scroll */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="live-only-cats">
          {LIVEMOA_CATEGORY_OPTIONS.map((c) => (
            <button key={c} type="button" onClick={() => setCat(c)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                cat === c ? 'bg-black text-white dark:bg-white dark:text-black border-transparent'
                          : 'bg-white dark:bg-[#141414] text-gray-600 border-black/10 dark:border-white/10 hover:border-black/40'}`}
              data-testid={`live-only-cat-${c}`}>{LIVEMOA_CATEGORY_LABEL[c]}</button>
          ))}
        </div>

        {filtered.length === 0 && !loading && (
          <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="live-only-empty">
            {q || cat !== 'all' ? '검색 결과가 없습니다.' : '현재 방송중인 LIVE 가 없습니다.'}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="live-only-grid">
          {filtered.map((it) => (<HomeLiveCard key={it.external_id} live={it} />))}
        </div>

        {hasMore && !q && cat === 'all' && (
          <div className="mt-10 flex justify-center">
            <button type="button" onClick={() => fetchPage(true)} disabled={loading}
              className="px-6 py-2.5 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-sm font-bold hover:border-black/40 disabled:opacity-50"
              data-testid="live-only-more">
              {loading ? '불러오는 중…' : '더보기'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default LiveOnly;
